import React from 'react';
import { Typography, Tooltip, theme } from 'antd';
import { motion } from 'framer-motion';
import { CORAL, CORAL_TINT_LIGHT, CORAL_SOFT_BG, SURFACE_1 } from '../theme/creamyTokens';

const { Text } = Typography;

/**
 * One series, drawn either as bars or as a line, with plain elements and one
 * inline `<svg>`.
 *
 * Deliberately not a charting library: this app has none, and pulling one in
 * for a single trend would add a dependency, a bundle and a second visual
 * language to maintain alongside the creamy one. A bar is a div with a
 * height; a line is a polyline.
 *
 * The two shapes share everything that is not the mark itself -- the scale,
 * the empty state, the labels, the tooltips, the highlighted "now" period,
 * the horizontal scroll when there are more periods than room. That is why
 * this is one component with a `variant` rather than two: switching between
 * them has to be a prop, not a different chart with its own idea of what the
 * maximum is.
 *
 * Marks arrive rather than appear -- bars grow from the baseline, the line
 * and its dots fade and pop in -- with the same soft spring the rest of the
 * app animates with (PayoutIssueBanner, NavBadge).
 */
export type MiniTrendVariant = 'bars' | 'line';

export interface IMiniTrendPoint {
  key: string;
  /** Under the mark. Kept to a few characters -- these sit at 11px and must not wrap. Empty string draws no label. */
  label: string;
  value: number;
  /** Shown on hover/long-press, where the full figure belongs. */
  tooltip?: React.ReactNode;
  /** The one period that is "now": solid instead of tinted. */
  highlighted?: boolean;
}

export interface IMiniTrendChartProps {
  points: IMiniTrendPoint[];
  variant?: MiniTrendVariant;
  /** Total height in px, labels included. */
  height?: number;
  /** Rendered instead of the marks when every value is zero -- a flat row of nothing reads as broken. */
  emptyLabel?: string;
}

/** Room under the plot for the labels. */
const LABEL_ROW = 22;
/**
 * Room ABOVE the plot for the top half of a dot.
 *
 * The dot for the highest period sits centred on the plot's own top edge, so
 * half of it lives outside. That half used to be cut off: this chart scrolls
 * sideways, and `overflow-x: auto` forces the browser to compute `overflow-y`
 * as `auto` too -- there is no such thing as scrolling one axis and letting
 * the other overflow. The container is exactly as tall as its content, so the
 * top half of that one dot was clipped, and half a ring reads as a stray hook
 * drawn over the peak.
 *
 * Reserved out of `height` rather than added to it, so the chart still takes
 * exactly the room its caller asked for. Applied in both variants, so
 * switching between them does not move the plot.
 */
const PLOT_TOP_INSET = 6;
/** Below this a period is too narrow to aim at, so the chart scrolls instead of squeezing. */
const MIN_COLUMN_WIDTH = 18;
/** Half the gap between two marks, applied inside each column so the columns themselves still tile exactly. */
const COLUMN_INSET = 3;

const MiniTrendChart: React.FC<IMiniTrendChartProps> = ({
  points,
  variant = 'bars',
  height = 140,
  emptyLabel,
}) => {
  const { token } = theme.useToken();

  const max = Math.max(...points.map((p) => p.value), 0);
  const plotHeight = height - LABEL_ROW - PLOT_TOP_INSET;

  if (points.length === 0 || max <= 0) {
    return (
      <div
        style={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: token.colorFillQuaternary,
          borderRadius: 12,
        }}
      >
        <Text type="secondary" style={{ fontSize: 13 }}>
          {emptyLabel}
        </Text>
      </div>
    );
  }

  // Every mark sits at the centre of its own column, bars and line alike, so
  // the two variants line up with each other and with the labels. The columns
  // tile the width exactly (no flex `gap`, the breathing room is padding
  // inside them) -- otherwise the gaps would push each centre off the
  // percentage the line is drawn at, and the line would drift away from the
  // labels a little more with every period.
  const centreOf = (index: number) => ((index + 0.5) / points.length) * 100;
  const heightOf = (value: number) => (value / max) * 100;

  const linePoints = points.map((point, index) => `${centreOf(index)},${100 - heightOf(point.value)}`).join(' ');
  const areaPoints = `${centreOf(0)},100 ${linePoints} ${centreOf(points.length - 1)},100`;

  return (
    <div style={{ overflowX: 'auto' }} className="no-scrollbar">
      {/* Holds the plot's own coordinate space. The SVG is sized against THIS,
          not the scroll container, so a chart wider than the screen still
          draws its line over the right columns. */}
      <div style={{ position: 'relative', height, minWidth: points.length * MIN_COLUMN_WIDTH }}>
        {variant === 'line' && (
          <svg
            width="100%"
            height={plotHeight}
            viewBox="0 0 100 100"
            // The plot is stretched to whatever width it ends up with, so the
            // percentages above map straight onto it. Strokes would stretch
            // with it, hence non-scaling-stroke below; and a circle would come
            // out an ellipse, which is why the dots are real elements over the
            // top rather than <circle>s in here.
            preserveAspectRatio="none"
            style={{ position: 'absolute', inset: `${PLOT_TOP_INSET}px 0 ${LABEL_ROW}px 0`, pointerEvents: 'none' }}
          >
            <motion.polygon
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              points={areaPoints}
              fill={CORAL_SOFT_BG}
            />
            {/* Fades in rather than drawing itself on. The draw-on effect is
                `pathLength`, which framer-motion implements as a
                stroke-dasharray in USER space -- and this viewBox is stretched
                unevenly (wide and short), with a stroke that deliberately does
                not stretch with it. So a dash that should be one unit long
                came out a different length on every segment: solid along the
                flat stretches, gone on some climbs, and a stray hook left
                sitting over a peak. Those hooks were the leftovers of an
                animation that never finished covering its own line. */}
            <motion.polyline
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              points={linePoints}
              fill="none"
              stroke={CORAL}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        )}

        <div style={{ display: 'flex', height: '100%' }}>
          {points.map((point, index) => (
            // The tooltip hangs off the whole column, not off the mark: a dot
            // is 8px of hit area and a thin bar not much more, which is
            // nothing to aim at with a finger.
            <Tooltip key={point.key} title={point.tooltip}>
              <div
                style={{
                  flex: '1 1 0',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  padding: `${PLOT_TOP_INSET}px ${COLUMN_INSET}px 0`,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <div style={{ position: 'relative', width: '100%', height: plotHeight }}>
                  {variant === 'bars' ? (
                    <div
                      style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'flex-end',
                        background: SURFACE_1,
                        borderRadius: 8,
                        overflow: 'hidden',
                      }}
                    >
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{
                          // A mark of literally zero is invisible, which reads
                          // as missing data rather than as a period that
                          // earned nothing -- so an empty period keeps a stub.
                          height: Math.max(2, Math.round((point.value / max) * plotHeight)),
                        }}
                        transition={{ type: 'spring', stiffness: 220, damping: 26 }}
                        style={{
                          width: '100%',
                          background: point.highlighted ? CORAL : CORAL_TINT_LIGHT,
                          borderRadius: 8,
                        }}
                      />
                    </div>
                  ) : (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 22, delay: index * 0.012 }}
                      style={{
                        position: 'absolute',
                        left: '50%',
                        bottom: `${heightOf(point.value)}%`,
                        transform: 'translate(-50%, 50%)',
                        width: point.highlighted ? 10 : 7,
                        height: point.highlighted ? 10 : 7,
                        borderRadius: '50%',
                        background: point.highlighted ? CORAL : token.colorBgContainer,
                        border: `2px solid ${CORAL}`,
                        boxSizing: 'border-box',
                      }}
                    />
                  )}
                </div>
                <Text
                  type="secondary"
                  style={{
                    fontSize: 11,
                    lineHeight: `${LABEL_ROW}px`,
                    whiteSpace: 'nowrap',
                    color: point.highlighted ? token.colorText : undefined,
                  }}
                >
                  {point.label}
                </Text>
              </div>
            </Tooltip>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MiniTrendChart;
